
import { supabase } from '@/lib/supabase';
import { supabaseCustomRole } from '@/lib/supabase/customRoleClient';
import { toast } from 'sonner';
import { isValidUserId } from '@/utils/authValidation';
import { favoritesCache } from '@/lib/cache/favoritesCache';
import { debugLog, debugError } from '@/utils/debugLogger';

/**
 * Check if the favorites table exists and is accessible
 */
const checkFavoritesTableExists = async () => {
  try {
    const { error: customRoleError } = await supabaseCustomRole
      .from('favorites')
      .select('count')
      .limit(0);

    if (!customRoleError) {
      return true;
    }

    const { error: mainClientError } = await supabase
      .from('favorites')
      .select('count')
      .limit(0);

    return !mainClientError;
  } catch (error) {
    console.error("Error checking favorites table:", error);
    return false;
  }
};

export const fetchFavorites = async (userId: string) => {
  if (!isValidUserId(userId)) {
    debugError("favorites", "Cannot fetch favorites: Invalid userId format", { userId }, "fetchFavorites");
    return [];
  }
  
  // Check cache first
  const cached = favoritesCache.get(userId);
  if (cached) {
    return cached;
  }

  // Use request deduplication to prevent multiple simultaneous requests
  return favoritesCache.deduplicate(userId, async () => {
    try {
      const tableExists = await checkFavoritesTableExists();
      if (!tableExists) {
        toast.error("Errore di configurazione", {
          description: "La tabella dei preferiti non è configurata correttamente."
        });
        return [];
      }

      let favoritesData;
      let favoritesError;
      
      try {
        const result = await supabase
          .from('favorites')
          .select('dealer_id')
          .eq('user_id', userId);
        
        favoritesData = result.data;
        favoritesError = result.error;
      } catch (mainClientError) {
        debugLog('favorites', 'Error using main client for favorites, trying custom role client', mainClientError, 'fetchFavorites');
        favoritesError = mainClientError;
      }
      
      if (favoritesError || !favoritesData) {
        const result = await supabaseCustomRole
          .from('favorites')
          .select('dealer_id')
          .eq('user_id', userId);
        
        favoritesData = result.data;
        favoritesError = result.error;
      }
      
      if (favoritesError) {
        debugError('favorites', 'Error fetching favorites', favoritesError, 'fetchFavorites');
        return [];
      }
      
      const favoriteIds = (favoritesData || []).map(fav => fav.dealer_id);
      
      // Cache the result
      favoritesCache.set(userId, favoriteIds);
      
      return favoriteIds;
    } catch (error) {
      debugError('favorites', 'Error in fetchFavorites', error, 'fetchFavorites');
      return [];
    }
  });
};

export const addToFavorites = async (userId: string, dealerId: string) => {
  if (!isValidUserId(userId) || !isValidUserId(dealerId)) {
    toast.error("Errore di autenticazione", {
      description: "ID non valido. Prova a effettuare nuovamente l'accesso."
    });
    throw new Error("Invalid ID format");
  }
  
  try {
    const existingFavorites = await fetchFavorites(userId);
    if (existingFavorites.includes(dealerId)) {
      toast.success("Già nei preferiti");
      return;
    }
    
    // Invalidate cache before adding
    favoritesCache.invalidate(userId);
    
    const tableExists = await checkFavoritesTableExists();
    if (!tableExists) {
      toast.error("Errore di configurazione", {
        description: "La tabella dei preferiti non è configurata correttamente."
      });
      throw new Error("Favorites table not configured");
    }

    let error;
    
    try {
      const result = await supabase
        .from('favorites')
        .insert([{ user_id: userId, dealer_id: dealerId }]);
        
      error = result.error;
      
      if (!error) {
        toast.success("Aggiunto ai preferiti");
        return;
      }
    } catch (mainClientError) {
      error = mainClientError;
    }
    
    if (error && error.code !== '23505') {
      const result = await supabaseCustomRole
        .from('favorites')
        .insert([{ user_id: userId, dealer_id: dealerId }]);
        
      error = result.error;
      
      if (error && error.code !== '23505') {
        throw error;
      }
    }
  } catch (error) {
    debugError('favorites', 'Error in addToFavorites', error, 'addToFavorites');
    // Invalidate cache on error
    favoritesCache.invalidate(userId);
    throw error;
  }
};

export const removeFromFavorites = async (userId: string, dealerId: string): Promise<boolean> => {
  debugLog('favorites', 'Starting removeFromFavorites operation', { userId, dealerId }, 'removeFromFavorites');
  
  if (!isValidUserId(userId) || !isValidUserId(dealerId)) {
    debugError('favorites', 'Invalid user or dealer ID format', { userId, dealerId }, 'removeFromFavorites');
    toast.error("Errore di autenticazione", {
      description: "ID non valido. Prova a effettuare nuovamente l'accesso."
    });
    return false;
  }
  
  try {
    const tableExists = await checkFavoritesTableExists();
    if (!tableExists) {
      debugError('favorites', 'Favorites table does not exist or is not accessible', undefined, 'removeFromFavorites');
      toast.error("Errore di configurazione", {
        description: "La tabella dei preferiti non è configurata correttamente."
      });
      return false;
    }

    let deleteResult;
    let deleteError;
    let rowsDeleted = 0;
    
    // Try with supabaseCustomRole first
    try {
      debugLog('favorites', 'Attempting DELETE with supabaseCustomRole', { userId, dealerId }, 'removeFromFavorites');
      deleteResult = await supabaseCustomRole
        .from('favorites')
        .delete()
        .eq('user_id', userId)
        .eq('dealer_id', dealerId)
        .select();
        
      deleteError = deleteResult.error;
      rowsDeleted = deleteResult.data?.length || 0;
      
      debugLog('favorites', 'supabaseCustomRole DELETE result', { 
        error: deleteError, 
        rowsDeleted,
        data: deleteResult.data 
      }, 'removeFromFavorites');
      
    } catch (customRoleError) {
      debugError('favorites', 'supabaseCustomRole DELETE failed', customRoleError, 'removeFromFavorites');
      deleteError = customRoleError;
    }
    
    // If custom role failed, try with main supabase client
    if (deleteError || rowsDeleted === 0) {
      try {
        debugLog('favorites', 'Attempting DELETE with main supabase client', { userId, dealerId }, 'removeFromFavorites');
        deleteResult = await supabase
          .from('favorites')
          .delete()
          .eq('user_id', userId)
          .eq('dealer_id', dealerId)
          .select();
          
        deleteError = deleteResult.error;
        rowsDeleted = deleteResult.data?.length || 0;
        
        debugLog('favorites', 'Main supabase DELETE result', { 
          error: deleteError, 
          rowsDeleted,
          data: deleteResult.data 
        }, 'removeFromFavorites');
        
      } catch (mainClientError) {
        debugError('favorites', 'Main supabase DELETE failed', mainClientError, 'removeFromFavorites');
        deleteError = mainClientError;
      }
    }
    
    // Check for errors
    if (deleteError) {
      debugError('favorites', 'DELETE operation failed with error', deleteError, 'removeFromFavorites');
      throw deleteError;
    }
    
    // Verify that the record was actually deleted
    if (rowsDeleted === 0) {
      debugLog('favorites', 'No rows were deleted - record may not have existed', { userId, dealerId }, 'removeFromFavorites');
      // Still consider this a success since the desired state (not in favorites) is achieved
      toast.success("Rimosso dai preferiti");
      return true;
    }
    
    // Verify deletion with a SELECT query
    try {
      const verificationResult = await supabase
        .from('favorites')
        .select('id')
        .eq('user_id', userId)
        .eq('dealer_id', dealerId)
        .maybeSingle();
      
      if (verificationResult.data) {
        debugError('favorites', 'Verification failed: record still exists after DELETE', verificationResult.data, 'removeFromFavorites');
        return false;
      }
      
      debugLog('favorites', 'Verification successful: record deleted', { rowsDeleted }, 'removeFromFavorites');
      
      // Only invalidate cache AFTER successful operation
      favoritesCache.invalidate(userId);
      toast.success("Rimosso dai preferiti");
      return true;
      
    } catch (verificationError) {
      debugError('favorites', 'Verification query failed', verificationError, 'removeFromFavorites');
      // If verification fails, assume success since the DELETE didn't error
      favoritesCache.invalidate(userId);
      toast.success("Rimosso dai preferiti");
      return true;
    }
    
  } catch (error) {
    debugError('favorites', 'removeFromFavorites operation failed', error, 'removeFromFavorites');
    
    // Don't invalidate cache on error - keep existing state
    if ((error as any)?.code === '42501' || (error as any)?.message?.includes('permission denied')) {
      toast.error("Errore di permessi", {
        description: "Non hai i permessi per rimuovere questo preferito."
      });
    } else {
      toast.error("Errore nella rimozione", {
        description: "Si è verificato un errore durante la rimozione dai preferiti."
      });
    }
    
    return false;
  }
};
